import React, { Component } from "react";
import { Modal } from "react-bootstrap";
import PropTypes from "prop-types";
import { FiCheckCircle, FiSave, FiX } from "react-icons/fi";

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Figtree:wght@400;500;600;700&display=swap');

  /* Stacked modals. This popup is opened FROM other modals (AddSoundModal,
     AddImageModal, VideoModal, InfographicSceneModal, ...). The app's dialog
     band (sass/style.scss) puts plain .modal at 3000000, AppModal
     (.pageModal.modal) at 3000030 and FileModal at 3000040, with overlays up
     to 3000090 — so this popup and its backdrop must sit above all of them.
     Bootstrap-3-era values (1080/1090) land far below the band and leave the
     popup hidden behind the dialog that opened it.
     NOTE: react-bootstrap 2 forwards "id" to the inner .modal-dialog, NOT to
     the fixed .modal wrapper that owns the stacking context — so the z-index
     must target the wrapper via "className" (.sf-modal). */
  .modal.sf-modal {
    z-index: 3000110;
  }

  .modal-backdrop.sf-backdrop {
    z-index: 3000100;
  }

  #serverModal .modal-content {
    font-family: 'Figtree', sans-serif;
    border: none;
    border-radius: 20px;
    box-shadow: 0 24px 60px rgba(0, 0, 0, 0.14);
    overflow: hidden;
    background: #ffffff;
    max-width: 420px;
    margin: 0 auto;
  }

  #serverModal .modal-header {
    background: transparent;
    border-bottom: none;
    padding: 20px 20px 0 20px;
    position: relative;
  }

  #serverModal .modal-body {
    padding: 0 28px 28px 28px;
  }

  .sf-close-btn {
    position: absolute;
    top: 16px;
    right: 16px;
    background: none;
    border: none;
    cursor: pointer;
    color: #9ca3af;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    transition: background 0.15s, color 0.15s;
    z-index: 2;
  }

  .sf-close-btn:hover {
    background: #f3f4f6;
    color: #374151;
  }

  .sf-icon-wrapper {
    position: relative;
    width: 80px;
    height: 80px;
    margin-bottom: 20px;
  }

  .sf-icon-ring-outer {
    position: absolute;
    inset: 0;
    border-radius: 50%;
    opacity: 0.12;
  }

  .sf-icon-ring-inner {
    position: absolute;
    inset: 12px;
    border-radius: 50%;
    opacity: 0.22;
  }

  .sf-icon-circle {
    position: absolute;
    inset: 20px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .sf-icon-circle svg {
    width: 22px;
    height: 22px;
  }

  /* Success colors */
  .sf-icon-wrapper.success .sf-icon-ring-outer { background: #22c55e; }
  .sf-icon-wrapper.success .sf-icon-ring-inner { background: #22c55e; }
  .sf-icon-wrapper.success .sf-icon-circle { background: #dcfce7; color: #16a34a; }

  /* Error colors */
  .sf-icon-wrapper.error .sf-icon-ring-outer { background: #f97316; }
  .sf-icon-wrapper.error .sf-icon-ring-inner { background: #f97316; }
  .sf-icon-wrapper.error .sf-icon-circle { background: #ffedd5; color: #ea580c; }

  .sf-title {
    font-size: 18px;
    font-weight: 700;
    color: #111827;
    margin: 0 0 8px 0;
    line-height: 1.3;
  }

  .sf-message {
    font-size: 14px;
    font-weight: 400;
    color: #6b7280;
    margin: 0 0 28px 0;
    line-height: 1.6;
  }

  .sf-actions {
    display: flex;
    gap: 12px;
  }

  .sf-btn {
    flex: 1;
    padding: 14px 20px;
    border-radius: 12px;
    font-family: 'Figtree', sans-serif;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.18s ease;
    border: none;
    outline: none;
  }

  .sf-btn-secondary {
    background: #ffffff;
    color: #374151;
    border: 1.5px solid #e5e7eb;
  }

  .sf-btn-secondary:hover {
    background: #f9fafb;
    border-color: #d1d5db;
  }

  .sf-btn-primary {
    background: #5b5bd6;
    color: #ffffff;
    box-shadow: 0 4px 14px rgba(91, 91, 214, 0.35);
  }

  .sf-btn-primary:hover {
    background: #4f4fbf;
    box-shadow: 0 6px 18px rgba(91, 91, 214, 0.45);
    transform: translateY(-1px);
  }

  .sf-btn-primary:active {
    transform: translateY(0);
  }
`;

export default class ServerFeedback extends Component {
  render() {
    const { isSuccess, show, hideModal, title, message, onConfirm, confirmLabel, cancelLabel } =
      this.props;

    const iconType = isSuccess ? "success" : "error";
    const IconComponent = isSuccess ? FiCheckCircle : FiSave;

    const renderMessage = () => {
      if (!message) return null;
      const isHTML =
        message.startsWith("<a") ||
        message.startsWith("<div") ||
        message.startsWith("<p") ||
        message.startsWith("<span");

      return isHTML ? (
        <div className="sf-message" dangerouslySetInnerHTML={{ __html: message }} />
      ) : (
        <p className="sf-message">{message}</p>
      );
    };

    return (
      <>
        <style>{styles}</style>
        <Modal
          id="serverModal"
          className="sf-modal"
          onHide={hideModal}
          show={show}
          dialogClassName="custom-modal"
          // Its own backdrop class so it can be lifted above the modal that
          // opened it — see the z-index note in `styles`.
          backdropClassName="sf-backdrop"
          centered
          // Başka bir Bootstrap Modal (örn. AddSoundModal) açıkken, default
          // enforceFocus parent'ın focus-trap'ini aktif tutar ve bu popup'ın
          // X butonu/backdrop tıklamaları "yutulur". Stacked modal senaryosunda
          // çalışabilmesi için focus zorlamasını kapatıyoruz.
          enforceFocus={false}
          restoreFocus={false}
        >
          <Modal.Header>
            <button className="sf-close-btn" onClick={hideModal} aria-label="Close">
              <FiX size={16} />
            </button>
          </Modal.Header>

          <Modal.Body>
            {/* Icon */}
            <div className={`sf-icon-wrapper ${iconType}`}>
              <div className="sf-icon-ring-outer" />
              <div className="sf-icon-ring-inner" />
              <div className="sf-icon-circle">
                <IconComponent size={22} />
              </div>
            </div>

            {/* Title */}
            {title && <h2 className="sf-title">{title}</h2>}

            {/* Message */}
            {renderMessage()}
          </Modal.Body>
        </Modal>
      </>
    );
  }
}

ServerFeedback.propTypes = {
  show: PropTypes.bool,
  title: PropTypes.any,
  message: PropTypes.any,
  isSuccess: PropTypes.bool,
  isBusy: PropTypes.any,
  hideModal: PropTypes.func.isRequired,
  onConfirm: PropTypes.func,
  confirmLabel: PropTypes.string,
  cancelLabel: PropTypes.string,
};
